import { type ColumnId } from '@blaze-shared/api-contracts-domain';
import {
  type ChartAggregateFunctionType,
  type ChartVariantType,
} from '@blaze-shared/elements-domain';
import { deepClone } from '@blaze-shared/shared-util';
import {
  findWorkflowNode,
  getDefaultFunctionAlias,
  isAggregateNode,
  isComputeNode,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { splitTimeToParts } from './labels.utils';

export type ComputeKeys = {
  year: string;
  month: string;
  details: string;
};

export function isComputeNodeForChart(node: WorkflowNode): boolean {
  return (
    !!findWorkflowNode([node], isAggregateNode, 'aggregate', 'chart-') ||
    !!findWorkflowNode([node], isComputeNode, undefined, 'chart-')
  );
}

export function getKeysToReadDataFromValue(srcRow: any): ComputeKeys {
  return srcRow
    ? {
        year: 'Computed_computeByYear' in srcRow ? 'Computed_computeByYear' : 'computeByYear',
        month: 'Computed_computeByMonth' in srcRow ? 'Computed_computeByMonth' : 'computeByMonth',
        details:
          'Computed_computeByDetails' in srcRow ? 'Computed_computeByDetails' : 'computeByDetails',
      }
    : {
        year: 'computeByYear',
        month: 'computeByMonth',
        details: 'computeByDetails',
      };
}
/**
 * Fixes data rows by replacing zeros with null for computed time-based fields.
 *
 * When using computeBy nodes, MongoDB automatically converts null values to 0.
 * Since 0 is not a valid value for time periods (year, month, details), this function
 * converts these zeros back to null to ensure proper data representation and visualization.
 *
 * @param dataRow - Array of data objects to process
 * @param computeKeys - Object containing the keys for year, month, and details fields
 * @returns Array of data objects with zeros converted to nulls for the specified compute keys
 */
export function fixDataRowsComputeZeros(dataRow: any[], computeKeys: ComputeKeys): any[] {
  const keysArray = [computeKeys.year, computeKeys.month, computeKeys.details];
  return dataRow.map((row) => {
    const newRow = deepClone(row);
    keysArray.forEach((key) => {
      if (newRow[key] === 0) {
        newRow[key] = null;
      }
    });
    return newRow;
  });
}

export function allDataWithTheSameYear(
  exampleRow: any,
  allRows: any[],
  computeKeys: ComputeKeys,
): boolean {
  return (
    exampleRow?.[computeKeys.year] &&
    allRows.filter((data) => data[computeKeys.year] !== exampleRow[computeKeys.year]).length === 0
  );
}
export function allDataWithTheSameMonth(
  exampleRow: any,
  allRows: any[],
  monthColumnName: string,
): boolean {
  return (
    exampleRow?.[monthColumnName] &&
    allRows.filter((data) => data[monthColumnName] !== exampleRow[monthColumnName]).length === 0
  );
}

export function getMonthColumnName(
  srcRow: any,
  computeKeys: ComputeKeys,
  groupOrSegmentDetails: boolean,
): string {
  return (
    srcRow &&
    (computeKeys.month in srcRow ? computeKeys.month : groupOrSegmentDetails && computeKeys.details)
  );
}

export function fillDataRowsWithMissingYears<T>(
  highestYear: number,
  lowestYear: number,
  fillClone: any,
  computeKeys: ComputeKeys,
  dataRows: any[],
): T[] {
  const newRangeArr = [...new Array(Math.round(highestYear - lowestYear + 1))].map(() =>
    deepClone(fillClone),
  );
  newRangeArr.forEach((r, index) => (r[computeKeys.year] = lowestYear + index));
  newRangeArr.pop();
  newRangeArr.shift();
  const results = [...dataRows];
  newRangeArr.forEach((yr) => {
    if (!results.some((el) => el[computeKeys.year] === yr[computeKeys.year])) {
      results.push(yr);
    }
  });
  return results;
}

export function fillDataRowsWithMissingQuarters<T>(
  highestYear: number,
  lowestYear: number,
  fillClone: any,
  computeKeys: ComputeKeys,
  dataRows: any[],
): T[] {
  const lowestQuarter = Math.min(
    ...dataRows
      .filter((r) => r[computeKeys.year] === lowestYear)
      .map((r) => r[computeKeys.details]),
  );
  const highestQuarter = Math.max(
    ...dataRows
      .filter((r) => r[computeKeys.year] === highestYear)
      .map((r) => r[computeKeys.details]),
  );

  const results = [...dataRows];
  if (!isNaN(highestYear) && !isNaN(lowestYear)) {
    let newRangeArr = [...new Array((highestYear - lowestYear + 1) * 4)].map(() =>
      deepClone(fillClone),
    );
    newRangeArr.forEach((r, index) => {
      r[computeKeys.year] = lowestYear + Math.floor(index / 4);
      r[computeKeys.details] = 1 + (index % 4);
    });
    newRangeArr = newRangeArr.filter(
      (row) =>
        !(
          (row[computeKeys.year] === lowestYear && row[computeKeys.details] <= lowestQuarter) ||
          (row[computeKeys.year] === highestYear && row[computeKeys.details] >= highestQuarter)
        ),
    );
    newRangeArr.forEach((r) => {
      if (
        !results.some(
          (el) =>
            r[computeKeys.year] === el[computeKeys.year] &&
            r[computeKeys.details] == el[computeKeys.details],
        )
      ) {
        results.push(r);
      }
    });
  }
  return results;
}

export function fillDataRowsWithMissingMonths<T>(
  highestYear: number,
  lowestYear: number,
  fillClone: any,
  computeKeys: ComputeKeys,
  monthColumnName: string,
  dataRows: any[],
): T[] {
  const lowestMonth = Math.min(
    ...dataRows.filter((d) => d[computeKeys.year] === lowestYear).map((d) => d[monthColumnName]),
  );
  const highestMonth = Math.max(
    ...dataRows.filter((d) => d[computeKeys.year] === highestYear).map((d) => d[monthColumnName]),
  );

  const results = [...dataRows];
  if (!isNaN(highestYear) && !isNaN(lowestYear)) {
    let newRangeArr = [...new Array(Math.round((highestYear - lowestYear + 1) * 12))].map(() =>
      deepClone(fillClone),
    );
    newRangeArr.forEach((r, index) => {
      r[computeKeys.year] = lowestYear + Math.floor(index / 12);
      r[computeKeys.details] = 1 + (index % 12);
    });
    newRangeArr = newRangeArr.filter(
      (row) =>
        !(
          (row[computeKeys.year] === lowestYear && row[computeKeys.details] <= lowestMonth) ||
          (row[computeKeys.year] === highestYear && row[computeKeys.details] >= highestMonth)
        ),
    );
    newRangeArr.forEach((r) => {
      if (
        !results.some(
          (el) =>
            r[computeKeys.year] === el[computeKeys.year] &&
            r[computeKeys.details] == el[computeKeys.details],
        )
      ) {
        results.push(r);
      }
    });
  }
  return results;
}

export function fillDataRowsWithMissingWeeks<T>(
  highestYear: number,
  lowestYear: number,
  fillClone: any,
  computeKeys: ComputeKeys,
  dataRows: any[],
): T[] {
  const lowestDate = Math.min(
    ...dataRows
      .filter((d) => d[computeKeys.year] === lowestYear)
      .map((d) => new Date(d[computeKeys.details]).getTime()),
  );
  const highestDate = Math.max(
    ...dataRows
      .filter((d) => d[computeKeys.year] === highestYear)
      .map((d) => new Date(d[computeKeys.details]).getTime()),
  );

  const oneWeek = 3600 * 24 * 7 * 1000;
  const representativeDay = lowestDate;
  const results = [...dataRows];
  if (!isNaN(highestDate) && !isNaN(lowestDate)) {
    let newRangeArr = [...new Array(Math.round((highestDate - lowestDate) / oneWeek) + 1)].map(() =>
      deepClone(fillClone),
    );
    newRangeArr.forEach((r, index) => {
      const newDate = new Date(representativeDay + oneWeek * index);
      r[computeKeys.year] = newDate.getFullYear();
      r[computeKeys.details] = newDate.toISOString();
    });
    newRangeArr = newRangeArr.filter(
      (row) =>
        !(
          (row[computeKeys.year] === lowestYear &&
            new Date(row[computeKeys.details]).getTime() <= lowestDate) ||
          (row[computeKeys.year] === highestYear &&
            new Date(row[computeKeys.details]).getTime() >= highestDate)
        ),
    );
    newRangeArr.forEach((r) => {
      if (
        !results.some(
          (el) =>
            new Date(r[computeKeys.details]).getTime() ===
            new Date(el[computeKeys.details]).getTime(),
        )
      ) {
        results.push(r);
      }
    });
  }
  return results;
}
export function fillDataRowsWithMissingDays<T>(
  highestYear: number,
  lowestYear: number,
  fillClone: any,
  computeKeys: ComputeKeys,
  monthColumnName: string,
  dataRows: any[],
): T[] {
  const lowestMonth = Math.min(
    ...dataRows.filter((d) => d[computeKeys.year] === lowestYear).map((d) => d[monthColumnName]),
  );
  const highestMonth = Math.max(
    ...dataRows.filter((d) => d[computeKeys.year] === highestYear).map((d) => d[monthColumnName]),
  );

  const lowestDay = Math.min(
    ...dataRows
      .filter((d) => d[computeKeys.year] === lowestYear && d[computeKeys.month] === lowestMonth)
      .map((d) => d[computeKeys.details]),
  );
  const highestDay = Math.min(
    ...dataRows
      .filter((d) => d[computeKeys.year] === highestYear && d[computeKeys.month] === highestMonth)
      .map((d) => d[computeKeys.details]),
  );

  const lowestDate = new Date(lowestYear, lowestMonth - 1, lowestDay).getTime();
  const highestDate = new Date(highestYear, highestMonth - 1, highestDay).getTime();
  const oneDay = 3600 * 24 * 1000;

  const results = [...dataRows];
  if (!isNaN(highestDate) && !isNaN(lowestDate)) {
    let newRangeArr = [...new Array(Math.round((highestDate - lowestDate) / oneDay) + 1)].map(() =>
      deepClone(fillClone),
    );
    newRangeArr.forEach((r, index) => {
      const newDate = new Date(lowestDate + oneDay * index);
      r[computeKeys.year] = newDate.getFullYear();
      r[computeKeys.month] = newDate.getMonth() + 1;
      r[computeKeys.details] = newDate.getDate();
    });
    newRangeArr = newRangeArr.filter(
      (row) =>
        !(
          (row[computeKeys.year] === lowestYear &&
            row[computeKeys.month] <= lowestMonth &&
            row[computeKeys.details] <= lowestDay) ||
          (row[computeKeys.year] === highestYear &&
            row[computeKeys.month] >= highestMonth &&
            row[computeKeys.details] >= highestDay)
        ),
    );
    newRangeArr.forEach((r) => {
      if (
        !results.some(
          (row) =>
            r[computeKeys.details] === row[computeKeys.details] &&
            r[computeKeys.month] === row[computeKeys.month] &&
            row[computeKeys.year] === r[computeKeys.year],
        )
      ) {
        results.push(r);
      }
    });
  }
  return results;
}

function toMinutes(p: { hours: number; minutes: number }): number {
  return p.hours * 60 + p.minutes;
}

function computeMinTimeDiff(sorted: any[], groupByColumnsName: string): number {
  let minDiff = Number.MAX_SAFE_INTEGER;
  for (let index = 1; index < sorted.length; index++) {
    const parts1 = splitTimeToParts(sorted[index - 1][groupByColumnsName]);
    const parts2 = splitTimeToParts(sorted[index][groupByColumnsName]);
    if (parts1 && parts2) {
      const diff = Math.abs(toMinutes(parts1) - toMinutes(parts2));
      if (diff > 0) {
        minDiff = Math.min(minDiff, diff);
        if (minDiff === 1) {
          break;
        }
      }
    }
  }
  return minDiff;
}

export function fillTimeRowsWithMissingMinutes<T>(
  dataRows: any[],
  groupByColumnsName: string,
  segmentByColumnName: string,
  fillClones: any[],
): { dataRows: T[]; timeDiff?: number } {
  if (dataRows.length <= 2) {
    return { dataRows, timeDiff: undefined };
  }

  const sorted = dataRows.toSorted((a, b) =>
    a[groupByColumnsName]?.localeCompare?.(b[groupByColumnsName]),
  );

  const minDiff = computeMinTimeDiff(sorted, groupByColumnsName);
  const minParts = splitTimeToParts(sorted[0][groupByColumnsName]);
  const maxParts = splitTimeToParts(sorted.at(-1)?.[groupByColumnsName]);

  if (!minParts || !maxParts) {
    return { dataRows, timeDiff: undefined };
  }

  const startMin = toMinutes(minParts);
  const slotCount = Math.round((toMinutes(maxParts) - startMin) / minDiff) + 1;
  const results = [...dataRows];

  for (const fillClone of fillClones) {
    for (let index = 0; index < slotCount; index++) {
      const newTime = startMin + minDiff * index;
      const h = Math.floor(newTime / 60)
        .toString()
        .padStart(2, '0');
      const m = (newTime % 60).toString().padStart(2, '0');
      const timeStr = `${h}:${m}:00`;
      if (
        !results.some(
          (row) =>
            row[groupByColumnsName] === timeStr &&
            row[segmentByColumnName] === fillClone[segmentByColumnName],
        )
      ) {
        results.push({ ...deepClone(fillClone), [groupByColumnsName]: timeStr });
      }
    }
  }

  return { dataRows: results, timeDiff: minDiff };
}

export function getMissingValueForChartType(chartType?: ChartVariantType): number {
  return chartType === 'chartDot' ? Number.NaN : 0;
}

export function valueFromWorkflow(
  mainValueColumnName: string | undefined,
  mainValueColId: ColumnId | undefined,
  aggregation: ChartAggregateFunctionType | undefined,
  dataRow: any,
  alias?: string | undefined,
  chartType?: ChartVariantType,
): number | undefined {
  if (
    !mainValueColumnName &&
    !aggregation &&
    typeof dataRow === 'object' &&
    Object.keys(dataRow).length > 0
  ) {
    const data = Object.keys(dataRow)[0];
    const splitterIndex = data.indexOf('_');
    aggregation = data.substring(0, splitterIndex) as ChartAggregateFunctionType;
    mainValueColumnName = data.substring(splitterIndex + 1);
  }
  if (alias && alias in dataRow) {
    return dataRow[alias];
  } else if ('chartAggregation' in dataRow) {
    return dataRow.chartAggregation;
  } else if ((!mainValueColumnName && aggregation !== 'COUNT') || !aggregation) {
    return undefined;
  } else if (`${aggregation}_${mainValueColumnName}` in dataRow) {
    // when {'MAX_colA': 100}
    return dataRow[`${aggregation}_${mainValueColumnName}`];
  } else if (mainValueColId && `${aggregation}_${mainValueColId}` in dataRow) {
    // when {'MAX_colId:uuid': 100} // TODO: (columns by id) - getColumnName (?) // TODO: remove when aggregation will use column name
    return dataRow[`${aggregation}_${mainValueColId}`];
  } else if (aggregation in dataRow) {
    // when {'COUNT': 100}
    return dataRow[aggregation];
  } else if (mainValueColumnName && mainValueColumnName in dataRow) {
    // when {'colA': 100}
    return dataRow[mainValueColumnName];
  } else if (mainValueColId && mainValueColId in dataRow) {
    // when {'colId:uuid': 100} // TODO: (columns by id) - getColumnName (?) // TODO: remove when aggregation will use column name
    return dataRow[mainValueColId];
  } else if (getDefaultFunctionAlias(aggregation, mainValueColumnName) in dataRow) {
    // when {RowCount:100} or {MaxColA}
    return dataRow[getDefaultFunctionAlias(aggregation, mainValueColumnName)];
  } else if (getDefaultFunctionAlias(aggregation, mainValueColId) in dataRow) {
    // when {MaxColId:uuid}
    return dataRow[getDefaultFunctionAlias(aggregation, mainValueColId)];
  }
  // 0 - we are probably filling blank spaces
  return getMissingValueForChartType(chartType);
}
